/**
 * Shared "resolved" state for drivers' hours violations (zeecore
 * driverViolationReviews.ts). A review records who resolved or reopened a
 * violation and why; it never removes the violation, which still counts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, RotateCcw } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/apiClient';

export type ViolationReviewAction = 'resolved' | 'reopened';

export interface ViolationReview {
  review_id: string;
  violation_key: string;
  driver_id: string;
  rule_id: string;
  occurred_on: string;
  action: ViolationReviewAction;
  note?: string;
  reviewed_by: string;
  reviewed_at: string;
}

export interface ViolationReviewState {
  violation_key: string;
  status: ViolationReviewAction;
  latest: ViolationReview;
  history: ViolationReview[];
}

export interface ReviewableViolation {
  rule_id?: string;
  date: string;
}

export function violationReviewKey(driverId: string, ruleId: string, date: string): string {
  return `${driverId}|${ruleId}|${String(date).slice(0, 10)}`;
}

export interface DriverViolationReviews {
  reviews: Map<string, ViolationReviewState>;
  /** True until the reviews for the current range have been read: "resolved" is not known yet. */
  loading: boolean;
  /** Set when the reviews could not be read; "resolved" is then unknown, not "none". */
  error: string | null;
  save: (input: { driver_id: string; rule_id: string; occurred_on: string; action: ViolationReviewAction; note?: string }) => Promise<void>;
}

/** Reviews for violations from `from` to `to` (ISO instants of UK day bounds), shared by the whole organisation. */
export function useDriverViolationReviews(range: { from: string; to: string } | null): DriverViolationReviews {
  const [reviews, setReviews] = useState<Map<string, ViolationReviewState>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // A slower response for an older period must not replace the current one.
  const seq = useRef(0);
  const from = range?.from;
  const to = range?.to;

  useEffect(() => {
    if (!from || !to) {
      // Nothing to read (demo data): no reviews, not "still checking".
      setLoading(false);
      return;
    }
    const mine = ++seq.current;
    setError(null);
    setLoading(true);
    authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversViolationReviews({ from, to }))
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { reviews?: ViolationReviewState[] };
        if (mine !== seq.current) return;
        setReviews(new Map((body.reviews ?? []).map((s) => [s.violation_key, s])));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (mine !== seq.current) return;
        setReviews(new Map());
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
  }, [from, to]);

  const save = useCallback<DriverViolationReviews['save']>(async (input) => {
    const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.driversViolationReviews(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    const { review } = (await res.json()) as { review: ViolationReview };
    setReviews((prev) => {
      const next = new Map(prev);
      const history = [...(prev.get(review.violation_key)?.history ?? []), review];
      next.set(review.violation_key, { violation_key: review.violation_key, status: review.action, latest: review, history });
      return next;
    });
  }, []);

  return { reviews, loading, error, save };
}

const reviewedAtFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
});

/** Resolve / reopen one violation, showing who last did which. */
export function ViolationReviewControl({
  driverId,
  violation,
  state,
}: {
  driverId: string;
  violation: ReviewableViolation;
  state: DriverViolationReviews;
}) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Without a rule id the violation has no stable key, and while reviews are
  // loading or failed to load we don't know whether it was resolved: offer
  // nothing, so nobody resolves a violation that is already resolved.
  if (!violation.rule_id || state.error) return null;
  if (state.loading) return <p className="mt-2 text-xs text-muted-foreground">Checking who resolved this…</p>;
  const ruleId = violation.rule_id;
  const current = state.reviews.get(violationReviewKey(driverId, ruleId, violation.date));
  const resolved = current?.status === 'resolved';

  const submit = async (action: ViolationReviewAction) => {
    setSaving(true);
    setSaveError(null);
    try {
      await state.save({ driver_id: driverId, rule_id: ruleId, occurred_on: String(violation.date).slice(0, 10), action, note: note.trim() || undefined });
      setEditing(false);
      setNote('');
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const who = current ? `${current.latest.reviewed_by}, ${reviewedAtFormat.format(new Date(current.latest.reviewed_at))}` : '';

  return (
    <div className="mt-2 flex flex-col gap-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
      {current ? (
        <p className={resolved ? 'text-success' : 'text-muted-foreground'}>
          {resolved ? 'Resolved' : 'Reopened'} by {who}
          {current.latest.note ? `: ${current.latest.note}` : ''}
        </p>
      ) : null}
      {editing ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder={resolved ? 'Why reopen? (optional)' : 'What was done? (optional)'}
            className="min-w-[12rem] flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground"
            aria-label="Review note"
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => submit(resolved ? 'reopened' : 'resolved')}
            className="rounded bg-secondary px-2.5 py-1 font-medium text-foreground hover:bg-secondary/80 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" disabled={saving} onClick={() => { setEditing(false); setNote(''); }} className="px-1.5 py-1 text-muted-foreground hover:text-foreground">
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex w-fit items-center gap-1.5 rounded border border-border px-2.5 py-1 font-medium text-foreground hover:bg-secondary/80"
        >
          {resolved ? <RotateCcw className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}
          {resolved ? 'Reopen' : 'Mark resolved'}
        </button>
      )}
      {saveError ? <p className="text-destructive">Couldn't save: {saveError}</p> : null}
    </div>
  );
}
